/*
Custom version of withAuthenticator,
see: https://github.com/aws-amplify/amplify-ui/blob/main/packages/react/src/components/Authenticator/withAuthenticator.tsx
*/
import React, { useCallback, useEffect } from "react";
import pino from "pino";
import { Amplify } from "aws-amplify";
import {
  fetchAuthSession,
  getCurrentUser,
  signOut,
  signInWithRedirect,
} from "aws-amplify/auth";
import { Hub, I18n } from "aws-amplify/utils";
import {
  Authenticator,
  Flex,
  Grid,
  useTheme,
  ThemeProvider,
  View,
  Image,
  translations,
} from "@aws-amplify/ui-react";
// import '@aws-amplify/ui-react/styles.css';
import { Dialog } from "@mui/material";
import components from "./components";
import services from "./services";
import Loading from "../../components/Misc/Loading";
import AmplifyBackgroundImage from "../../img/amplify-background-person-laptop-nualas.jpeg";
import {
  getUsernameFromAuthData,
  trackPageView,
  searchStringToObj,
  objToSearchString,
  trackRecommendedEvent,
  trackEvent,
} from "../../utils/index";
import config from "../../config";

const logger = pino({ name: "Amplify", level: "info" });

const amplifyConfig = config.amplify;

/* check if env is localhost or not
if you're not developing on localhost,
you will need to detect this is another way—the docs linked above give some examples.
*/
const isLocalhost = !!(window.location.hostname === "localhost");

// split redirect signin and signout strings into correct URIs
const [productionRedirectSignIn, localRedirectSignIn] =
  amplifyConfig.oauth.redirectSignIn.split(",");
const [productionRedirectSignOut, localRedirectSignOut] =
  amplifyConfig.oauth.redirectSignOut.split(",");

// use correct URI in the right env
const updatedAmplifyConfig = {
  ...amplifyConfig,
  oauth: {
    ...amplifyConfig.oauth,
    redirectSignIn: isLocalhost
      ? localRedirectSignIn
      : productionRedirectSignIn,
    redirectSignOut: isLocalhost
      ? localRedirectSignOut
      : productionRedirectSignOut,
  },
};

Amplify.configure(updatedAmplifyConfig);

I18n.putVocabularies(translations);

const dict = {
  en: {
    "Enter your Given Name": "Enter your First Name",
    "UserMigration failed with error Bad credentials.":
      "Incorrect username or password.",
    "We Texted You": "We Emailed You",
    "Sign in": "Sign In",
    "An account with the given email already exists.":
      "Incorrect username or password.",
  },
  de: {
    "UserMigration failed with error Bad credentials.":
      translations.de["Incorrect username or password."],
    "We Texted You": translations.de["We Emailed You"],
    "Sign in": translations.de["Sign In"],
    "An account with the given email already exists.":
      translations.de["Incorrect username or password."],
  },
  es: {
    "UserMigration failed with error Bad credentials.":
      translations.es["Incorrect username or password."],
    "We Texted You": translations.es["We Emailed You"],
    "Sign in": translations.es["Sign In"],
    "An account with the given email already exists.":
      translations.es["Incorrect username or password."],
  },
  fr: {
    "Sign Up with Google": `S'inscrire  avec Google`,
    "UserMigration failed with error Bad credentials.":
      translations.fr["Incorrect username or password."],
    "We Texted You": translations.fr["We Emailed You"],
    "Sign in": translations.fr["Sign In"],
    "An account with the given email already exists.":
      translations.fr["Incorrect username or password."],
  },
  id: {
    "UserMigration failed with error Bad credentials.":
      translations.id["Incorrect username or password."],
    "We Texted You": translations.id["We Emailed You"],
    "Sign in": translations.id["Sign In"],
    "An account with the given email already exists.":
      translations.id["Incorrect username or password."],
  },
  it: {
    "UserMigration failed with error Bad credentials.":
      translations.it["Incorrect username or password."],
    "We Texted You": translations.it["We Emailed You"],
    "Sign in": translations.it["Sign In"],
    "An account with the given email already exists.":
      translations.it["Incorrect username or password."],
  },
  ja: {
    "UserMigration failed with error Bad credentials.":
      translations.ja["Incorrect username or password."],
    "We Texted You": translations.ja["We Emailed You"],
    "Sign in": translations.ja["Sign In"],
    "An account with the given email already exists.":
      translations.ja["Incorrect username or password."],
  },
  kr: {
    "UserMigration failed with error Bad credentials.":
      translations.kr["Incorrect username or password."],
    "We Texted You": translations.kr["We Emailed You"],
    "Sign in": translations.kr["Sign In"],
    "An account with the given email already exists.":
      translations.kr["Incorrect username or password."],
  },
  nl: {
    "UserMigration failed with error Bad credentials.":
      translations.nl["Incorrect username or password."],
    "We Texted You": translations.nl["We Emailed You"],
    "Sign in": translations.nl["Sign In"],
    "An account with the given email already exists.":
      translations.nl["Incorrect username or password."],
  },
  pl: {
    "UserMigration failed with error Bad credentials.":
      translations.pl["Incorrect username or password."],
    "We Texted You": translations.pl["We Emailed You"],
    "Sign in": translations.pl["Sign In"],
    "An account with the given email already exists.":
      translations.pl["Incorrect username or password."],
  },
  pt: {
    "UserMigration failed with error Bad credentials.":
      translations.pt["Incorrect username or password."],
    "We Texted You": translations.pt["We Emailed You"],
    "Sign in": translations.pt["Sign In"],
    "An account with the given email already exists.":
      translations.pt["Incorrect username or password."],
  },
  zh: {
    "UserMigration failed with error Bad credentials.":
      translations.zh["Incorrect username or password."],
    "We Texted You": translations.zh["We Emailed You"],
    "Sign in": translations.zh["Sign In"],
    "An account with the given email already exists.":
      translations.zh["Incorrect username or password."],
  },
  sv: {
    "UserMigration failed with error Bad credentials.":
      translations.sv["Incorrect username or password."],
    "We Texted You": translations.sv["We Emailed You"],
    "Sign in": translations.sv["Sign In"],
    "An account with the given email already exists.":
      translations.sv["Incorrect username or password."],
  },
  tr: {
    "UserMigration failed with error Bad credentials.":
      translations.tr["Incorrect username or password."],
    "We Texted You": translations.tr["We Emailed You"],
    "Sign in": translations.tr["Sign In"],
    "An account with the given email already exists.":
      translations.tr["Incorrect username or password."],
  },
  ru: {
    // No ru translation for 'Incorrect username or password.'
    "UserMigration failed with error Bad credentials.":
      "Incorrect username or password.",
    "We Texted You": "We Emailed You",
    "Sign in": "Sign In",
    "An account with the given email already exists.":
      "Incorrect username or password.",
  },
};
I18n.putVocabularies(dict);

const validIdPs = ["Wayside-SAML"];
const validActions = ["signIn", "signUp", "forgotPassword"];

function AmplifyAuthenticator(props) {
  const { tokens } = useTheme();

  useEffect(() => {
    trackPageView(`${window.location.pathname}${window.location.search}`);
  }, []);

  return (
    <Grid
      templateColumns={{
        base: "0 1fr",
        xl: "1fr 40%",
      }}
    >
      <View height="100vh">
        <Image
          src={AmplifyBackgroundImage}
          alt="Person on laptop with Nualas"
          width="100%"
          height="100%"
          objectFit="cover"
        />
      </View>
      <Flex
        backgroundColor={tokens.colors.background.secondary}
        justifyContent="center"
        style={{
          height: "100vh",
          overflowY: "auto",
        }}
      >
        <ThemeProvider>
          <Authenticator {...props}>
            {(authenticator) => <Loading {...props} {...authenticator} />}
          </Authenticator>
        </ThemeProvider>
      </Flex>
    </Grid>
  );
}

function UnauthenticatedComponentWrapper({
  initialState,
  isAuthDialogOpen,
  setIsAuthDialogOpen,
  formFields,
  options,
  children,
}) {
  return (
    <>
      <Dialog
        open={isAuthDialogOpen}
        fullScreen
        handleClose={() => setIsAuthDialogOpen(false)}
        aria-label="Authentication Dialog"
      >
        <AmplifyAuthenticator
          initialState={initialState}
          components={components}
          services={services}
          signUpAttributes={["given_name", "family_name"]}
          socialProviders={["google"]}
          formFields={formFields}
          {...options}
        />
      </Dialog>
      {children}
    </>
  );
}

export default function withAuthenticator(Component, options) {
  return function WrappedWithAuthenticator(props) {
    const [authState, setAuthState] = React.useState();
    const [userState, setUserState] = React.useState();
    const [loading, setLoading] = React.useState(true);

    const qsParams =
      typeof window !== "undefined"
        ? searchStringToObj(window.location.search)
        : {};
    const initialState = validActions.includes(qsParams.action)
      ? qsParams.action
      : "signIn";
    const userId = qsParams.userId;
    const userEmail = qsParams.email || "";
    const [isAuthDialogOpen, setIsAuthDialogOpen] = React.useState(
      validActions.includes(qsParams.action) ? true : false,
    );
    const [idP, setIdP] = React.useState(
      validIdPs.includes(qsParams.idP) ? qsParams.idP : "",
    );
    const federatedSwitchUserPath = sessionStorage.getItem(
      "federated-switch-user-path",
    );
    const federatedSwitchUserIdPValue = sessionStorage.getItem(
      "federated-switch-user-idp",
    );
    const federatedSwitchUserIdP = validIdPs.includes(
      federatedSwitchUserIdPValue,
    )
      ? federatedSwitchUserIdPValue
      : "";
    const isSwitchingUser =
      !!federatedSwitchUserPath && !!federatedSwitchUserIdP;

    async function setUser() {
      try {
        const { username } = await getCurrentUser();
        let user;
        if (username) {
          const session = await fetchAuthSession({ forceRefresh: true });
          const attributes = session?.tokens?.idToken?.payload;
          user = { username, attributes };
          if (user && user.attributes) {
            const selectedUsername = getUsernameFromAuthData(user);
            // add 'username' to attributes
            user.attributes.username = selectedUsername;
            setUserState({
              ...user,
              username,
            });
            setAuthState("signedin");
            setLoading(false);
          }
        }
      } catch (err) {
        setLoading(false);
        logger.error(err);
      }
    }

    const checkUser = useCallback(() => {
      setUser();
    }, []);

    useEffect(() => {
      /*
        Only skip saving the path while we're on the Cognito OAuth callback
      */
      const currentSearchParams = searchStringToObj(window.location.search);
      const isOAuthCallback =
        "state" in currentSearchParams &&
        ("code" in currentSearchParams || "error" in currentSearchParams);
      if (!isOAuthCallback) {
        if (window.location.search.includes("idP")) {
          /* 
            Dont include the 'idP' param in the current path.
            we don't want it included in the url when the user returns from the idP.
          */
          let searchParams = searchStringToObj(window.location.search);
          delete searchParams["idP"];
          delete searchParams["userId"];
          let searchString =
            objToSearchString(searchParams).length > 0
              ? "?" + objToSearchString(searchParams)
              : "";
          localStorage.setItem(
            "nualang-auth-current-path",
            `${window.location.pathname}${searchString}`,
          );
        } else {
          localStorage.setItem(
            "nualang-auth-current-path",
            `${window.location.pathname}${window.location.search}`,
          );
        }
      }
      // eslint-disable-next-line consistent-return
      const hubListener = Hub.listen("auth", ({ payload }) => {
        // A lot of these auth events have been removed from the Hub API in Amplify v6, so we need to find a way to replace them.
        if (payload.event === "signedIn") {
          const username = payload.data.username;
          if (username.startsWith("Wayside") || username.startsWith("Google")) {
            trackRecommendedEvent("login", {
              method: username.startsWith("Wayside")
                ? "Wayside-SAML"
                : "Google",
            });
          } else {
            trackRecommendedEvent("login", {
              method: "EmailAndPassword",
            });
          }
          return checkUser();
        }
        if (payload.event === "signedOut") {
          setUserState(null);
          return setLoading(false);
        }
        if (
          payload.event === "signIn_failure" &&
          payload.message === "The OAuth response flow failed" &&
          payload.data &&
          payload.data.message &&
          payload.data.message.startsWith("Already+found+an+entry+for+username")
        ) {
          trackEvent("login", "signIn_failure", payload.message);
          alert(
            "An account already exists with that email address, Please sign in with google again.",
          );
          // Auth.federatedSignIn({ provider: 'Google' });
        } else if (payload.event === "signIn_failure") {
          trackEvent(
            "login",
            "signIn_failure",
            payload && payload.data ? payload.data.message : "failed to signin",
          );
        }
        if (payload.event === "forgotPassword") {
          trackEvent("login", "forgotPassword");
        }
        if (payload.event === "forgotPasswordSubmit") {
          trackEvent("login", "forgotPasswordSubmit");
        }
        if (payload.event === "forgotPasswordSubmit_failure") {
          trackEvent(
            "login",
            "forgotPasswordSubmit_failure",
            payload && payload.data
              ? payload.data.message
              : "failed to reset password",
          );
        }
        if (payload.event === "signUp") {
          trackRecommendedEvent("sign_up", {
            method: "EmailAndPassword",
          });
        }
        if (payload.event === "confirmSignUp") {
          trackEvent("signUp", "confirmSignUp");
        }
      });

      checkUser();

      return () => {
        if (hubListener) {
          hubListener();
        }
      };
    }, [checkUser]);

    const isSignedIn = authState === "signedin" && userState ? true : false;

    function handleFederatedSignIn() {
      try {
        if (isSignedIn && userState && userState.username) {
          if (`${idP}_${userId}` !== userState.username) {
            /*
            If there is currently a different user signed in
            we need to sign them out before signing the new user in.
            We need to keep track of the path since the signOut causes a redirect and we lose the current path.
            */
            sessionStorage.setItem("federated-switch-user-idp", idP);
            sessionStorage.setItem(
              "federated-switch-user-path",
              localStorage.getItem("nualang-auth-current-path"),
            );
            signOut().then(() => {
              if (!userState.username.includes("_")) {
                /* 
                  Redirect when signing out a user that isn't from an IdP to match the experience of
                  Google, Wayside etc. If a username includes '_' is means it's from an IdP e.g Google_123 etc
                */
                config.keysToRemove.forEach((key) =>
                  localStorage.removeItem(key),
                );
                window.location.replace("/");
              }
            });
          } else {
            let searchParams = searchStringToObj(window.location.search);
            delete searchParams["idP"];
            delete searchParams["userId"];
            window.history.replaceState(
              null,
              "",
              "?" + objToSearchString(searchParams) + window.location.hash,
            );
            setIdP("");
          }
        } else {
          sessionStorage.setItem(
            "last-federated-user-path",
            localStorage.getItem("nualang-auth-current-path"),
          );
          sessionStorage.setItem("last-federated-user-idp", idP);
          signInWithRedirect({ provider: { custom: idP } });
        }
      } catch (err) {
        logger.error(err);
      }
    }

    useEffect(() => {
      let signInTimer;
      const redirectPathQueryParam = (federatedSwitchUserPath || "").includes(
        "?",
      )
        ? `&idP=${federatedSwitchUserIdP}`
        : `?idP=${federatedSwitchUserIdP}`;
      const redirectPath = `${federatedSwitchUserPath}${redirectPathQueryParam}`;
      if (idP && !loading) {
        sessionStorage.removeItem("federated-switch-user-path");
        sessionStorage.removeItem("federated-switch-user-idp");
        handleFederatedSignIn();
      } else if (isSwitchingUser && !loading && !isSignedIn) {
        if (
          `${window.location.pathname}${window.location.search}` !==
          redirectPath
        ) {
          sessionStorage.removeItem("federated-switch-user-path");
          sessionStorage.removeItem("federated-switch-user-idp");
          signInTimer = setTimeout(() => {
            window.location.replace(redirectPath);
          }, 500);
        }
      }
      return () => {
        if (signInTimer) {
          clearTimeout(signInTimer);
        }
      };
    }, [idP, loading]);

    useEffect(() => {
      function handleAmplifyError() {
        const cognitoIdentitySP = `CognitoIdentityServiceProvider.${process.env.REACT_APP_CLIENT_ID}`;
        const lastAuthUser = localStorage.getItem(
          `${cognitoIdentitySP}.LastAuthUser`,
        );
        if (lastAuthUser) {
          const userDataItemName = `${cognitoIdentitySP}.${lastAuthUser}.userData`;
          const userData = localStorage.getItem(userDataItemName);
          if (userData === "{}") {
            /*
            Amplify issue https://github.com/aws-amplify/amplify-js/issues/11106
            Sign out to clear local storage and prevent infinite loading screen
            */
            logger.info(
              "Encountered potential amplify error - Clearing userData in localstorage",
            );
            localStorage.removeItem(userDataItemName);
            // const lastUserIdP = sessionStorage.getItem('last-federated-user-idp');
            // const lastUserPath = sessionStorage.getItem('last-federated-user-path');
            // if (lastUserIdP && lastUserPath) {
            //   // Set following values to retry sign in once the sign out has ended
            //   sessionStorage.setItem('federated-switch-user-idp', lastUserIdP);
            //   sessionStorage.setItem('federated-switch-user-path', lastUserPath);
            // }
            // signOut();
          }
        }
      }
      handleAmplifyError();
    }, []);

    // show loading screen while fetching, otherwise return page
    if (loading || idP || isSwitchingUser) return <Loading />;

    return isSignedIn ? (
      <Component authData={userState} authenticated={true} {...props} />
    ) : (
      <>
        <UnauthenticatedComponentWrapper
          setIsAuthDialogOpen={setIsAuthDialogOpen}
          isAuthDialogOpen={isAuthDialogOpen}
          options={options}
          initialState={initialState}
          formFields={{
            ...(userEmail
              ? {
                  signIn: {
                    username: {
                      defaultValue: userEmail,
                    },
                  },
                }
              : {}),
            signUp: {
              given_name: {
                label: "First Name",
                placeholder: "Enter your First Name",
                order: 1,
              },
              family_name: {
                label: "Last Name",
                placeholder: "Enter your Last Name",
                order: 2,
              },
              ...(userEmail
                ? {
                    email: {
                      defaultValue: userEmail,
                    },
                  }
                : {}),
            },
          }}
        >
          <Component
            authData={{ attributes: {} }}
            setIsAuthDialogOpen={setIsAuthDialogOpen}
            isAuthDialogOpen={isAuthDialogOpen}
            authenticated={false}
            {...props}
          />
        </UnauthenticatedComponentWrapper>
      </>
    );
  };
}
